// src/components/ui/Modal.jsx
//
// Primitives layer, phase 1. Extracted from RegisterPage.jsx's own
// LegalModal -- already the right shape (fixed backdrop, centered card,
// icon + title header, close button, click-outside-to-close) -- this
// generalizes it into a reusable primitive rather than inventing a new
// pattern. Adds Escape-to-close and focus-trap-on-open, which the
// original didn't have -- a real accessibility gap in every hand-rolled
// modal this replaces (ReportModal.jsx, AvatarSelectorModal.jsx, and the
// various Group*Modal.jsx components all independently reimplement the
// backdrop/card shell with no Escape handling).
import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function Modal({
  open,
  onClose,
  title,
  icon: Icon,
  children,
  footer,
  maxWidth = 'max-w-2xl',
}) {
  const { t } = useTranslation();
  const cardRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    // Focus the card itself so Escape/Tab work immediately without
    // requiring a prior click inside the modal.
    cardRef.current?.focus();
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        ref={cardRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`rounded-2xl w-full ${maxWidth} max-h-[85vh] overflow-y-auto relative bg-white dark:bg-[#0d0d1a] border border-gray-200 dark:border-white/10 outline-none`}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || onClose) && (
          <div className="sticky top-0 p-6 flex items-center justify-between bg-white dark:bg-[#0d0d1a] border-b border-gray-200 dark:border-white/10">
            <div className="flex items-center gap-3 min-w-0">
              {Icon && (
                <div className="p-2 rounded-lg bg-violet-100 dark:bg-violet-500/15 flex-shrink-0">
                  <Icon className="text-violet-600 dark:text-violet-400" size={24} />
                </div>
              )}
              {title && (
                <h3 className="text-xl font-bold text-gray-900 dark:text-white truncate">{title}</h3>
              )}
            </div>
            {onClose && (
              <button
                onClick={onClose}
                aria-label={t('common.close')}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-white transition-colors flex-shrink-0 ml-3"
              >
                <X size={22} />
              </button>
            )}
          </div>
        )}

        <div className="p-6">{children}</div>

        {footer && (
          <div className="p-6 pt-0 flex gap-3">{footer}</div>
        )}
      </div>
    </div>
  );
}
